import mapboxgl from "mapbox-gl";

type MapboxPropertyValue = Maybe<Primitive> | mapboxgl.Expression;

/**
 * Safely sets a paint property on a Mapbox layer if it exists
 * @param map - Mapbox map instance
 * @param layerId - The layer ID to update
 * @param propertyName - The paint property name (e.g., 'fill-opacity', 'line-color')
 * @param value - The property value to set
 * @returns true if the property was set, false if the layer doesn't exist
 */
export function setPaintProperty(
  map: mapboxgl.Map,
  layerId: string,
  propertyName: string,
  value: MapboxPropertyValue
): boolean {
  if (!map || !layerId) return false;

  if (map.getLayer(layerId)) {
    map.setPaintProperty(layerId, propertyName, value);
    return true;
  }
  return false;
}

/**
 * Safely sets filter on a Mapbox layer if it exists
 * @param map - Mapbox map instance
 * @param layerId - The layer ID to update
 * @param filter - The filter expression
 * @returns true if the filter was set, false if the layer doesn't exist
 */
export function setFilter(
  map: mapboxgl.Map,
  layerId: string,
  filter: unknown[] | null | undefined
): boolean {
  if (!map || !layerId) return false;

  if (map.getLayer(layerId)) {
    map.setFilter(layerId, filter);
    return true;
  }
  return false;
}

/**
 * Checks if a source exists on the map
 * @param map - Mapbox map instance
 * @param sourceId - The source ID to check
 * @returns true if the source exists, false otherwise
 */
export function hasSource(map: mapboxgl.Map, sourceId: string): boolean {
  if (!map || !sourceId) return false;
  return !!map.getSource(sourceId);
}

/**
 * Safely removes a layer from the map if it exists
 * @param map - Mapbox map instance
 * @param layerId - The layer ID to remove
 * @returns true if the layer was removed, false if it didn't exist
 */
export function safeRemoveLayer(map: mapboxgl.Map, layerId: string): boolean {
  if (!map || !layerId) return false;

  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
    return true;
  }
  return false;
}

/**
 * Safely removes a layer and its source from the map if they exist
 * Removes the layer first, then the source (Mapbox requirement)
 * @param map - Mapbox map instance
 * @param id - The ID used for both layer and source
 * @returns Object indicating what was removed
 */
export function safeRemoveLayerAndSource(
  map: mapboxgl.Map,
  id: string
): { layerRemoved: boolean; sourceRemoved: boolean } {
  if (!map || !id) return { layerRemoved: false, sourceRemoved: false };

  const layerRemoved = safeRemoveLayer(map, id);
  const sourceRemoved = hasSource(map, id);
  if (sourceRemoved) map.removeSource(id);

  return { layerRemoved, sourceRemoved };
}

/**
 * Safely removes multiple layers from the map
 * @param map - Mapbox map instance
 * @param layerIds - Array of layer IDs to remove
 * @returns Array of layer IDs that were successfully removed
 */
export function safeRemoveLayers(map: mapboxgl.Map, layerIds: string[]): string[] {
  if (!map || !layerIds?.length) return [];

  const removedLayers: string[] = [];

  for (const layerId of layerIds) {
    if (safeRemoveLayer(map, layerId)) {
      removedLayers.push(layerId);
    }
  }

  return removedLayers;
}

/**
 * Safely moves a layer to a new position in the rendering order
 * If beforeId is not provided, moves the layer to the top of the rendering order
 * @param map - Mapbox map instance
 * @param layerId - The layer ID to move
 * @param beforeId - Optional layer ID to insert before (if omitted, layer moves to top)
 * @returns true if the layer was moved, false if it doesn't exist or move failed
 */
export function safeMoveLayer(
  map: mapboxgl.Map,
  layerId: string,
  beforeId?: string
): boolean {
  if (!map || !layerId) return false;

  if (!map.getLayer(layerId)) {
    return false;
  }

  // If beforeId is provided, check if it exists
  if (beforeId && !map.getLayer(beforeId)) {
    return false;
  }

  try {
    map.moveLayer(layerId, beforeId);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * Safely moves multiple layers to new positions
 * Each layer is moved to the top of rendering order (unless they have errors)
 * Useful for batch operations like moving all draw layers to the top
 * @param map - Mapbox map instance
 * @param layerIds - Array of layer IDs to move
 * @param beforeId - Optional layer ID to insert all layers before
 * @returns Array of layer IDs that were successfully moved
 */
export function safeMoveLayers(
  map: mapboxgl.Map,
  layerIds: string[],
  beforeId?: string
): string[] {
  if (!map || !layerIds?.length) return [];

  const movedLayers: string[] = [];

  for (const layerId of layerIds) {
    if (safeMoveLayer(map, layerId, beforeId)) {
      movedLayers.push(layerId);
    }
  }

  return movedLayers;
}
